import * as React from 'react';
import * as styles from './team-dropdown.module.scss';
import { AppContext, AppProviderDispatch } from '../../shared/app-context';
import MaterialIcon from '@material/react-material-icon';
import BaseClient from '../../models/base-client';
import { Scrollbars } from 'react-custom-scrollbars';
import { useOktaAuth } from '@okta/okta-react';
import { Avatar, List, ListItem, ListItemAvatar, ListItemText, Popover } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import { fontFamilies } from '../../theme/text';
import { useHistory } from 'react-router';
import Paths from '../../models/enums/paths';
import { useClients } from '../../hooks/useClients';

interface Props {
  clientId: number;
  collapsed: boolean;
}

/** MUI Overrides */
const useStyles = makeStyles({
  avatar: {
    width: 32,
    height: 32,
    '& .MuiAvatar-img': {
      objectFit: 'contain'
    }
  },
  listItemText: {
    '& .MuiListItemText-primary': {
      fontSize: 18,
      fontFamily: fontFamilies.heading
    }
  },
  buttonStyle: {
    paddingLeft: 0,
    border: 0,
    background: 'none',
    color: '#ffffff',
    width: '100%'
  },
  arrowStyle: {
    flex: 3,
    textAlign: 'right'
  }
});

/**
 * @function TeamDropdown - Component for the team dropdown menu used in the left toolbar
 */
const TeamDropdown = ({ clientId, collapsed }: Props) => {
  const classes = useStyles();
  const appContext = React.useContext<AppProviderDispatch>(AppContext);
  const clientsQuery = useClients();
  const [currentClient, setCurrentClient] = React.useState<BaseClient>(null);
  const { authState } = useOktaAuth();
  const history = useHistory();

  // Anchor element of popover menu
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  React.useEffect(() => {
    if (clientsQuery.data && appContext.state.clientId) {
      setCurrentClient(clientsQuery.data.results.find((client) => client.clientId === Number(clientId)));
    }
  }, [clientsQuery.data, appContext.state.clientId]);

  /**
   * @function setActiveClient - Sets the active client in context and local storage after the client selects a client from the drop down menu
   */
  const setActiveClient = (client: BaseClient) => {
    localStorage.setItem('clientId', client.clientId.toString());
    localStorage.setItem('clientName', client.name);
    localStorage.setItem('enableServiceDesk', client.enableServiceDesk.toString());
    appContext.setState({
      clientId: client.clientId,
      clientName: client.name,
      enableServiceDesk: client.enableServiceDesk,
      clientChanging: true
    });
    handleClose();
    history.push(Paths.Reports);
  };

  return (
    <div className="team">
      {currentClient && (
        <button
          id={currentClient.clientId.toString()}
          key={currentClient.clientId.toString()}
          className={clsx(
            classes.buttonStyle,
            styles.activeClient,
            clientsQuery.data && clientsQuery.data.results.length <= 1 && styles.singleClient,
            collapsed && styles.collapsed
          )}
          onClick={handleOpen}
        >
          <div className={styles.imageHolder}>
            <img src={currentClient.brandImage + '&accessToken=' + authState.accessToken.accessToken} />
          </div>
          <span className={styles.content}>{currentClient.displayName}</span>
          <MaterialIcon className={clsx(styles.arrowDropDown, classes.arrowStyle)} icon="arrow_drop_down" />
        </button>
      )}
      <Popover
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        {authState.accessToken && clientsQuery.data && (
          <Scrollbars style={{ width: 280 }} autoHeight autoHeightMax={`calc(100vh - 165px)`}>
            <List>
              {clientsQuery.data.results.map((client) => {
                return (
                  <ListItem
                    key={`client-${client.clientId}`}
                    selected={client.clientId === Number(appContext.state.clientId)}
                    onClick={() => setActiveClient(client)}
                    button
                    dense
                  >
                    <ListItemAvatar>
                      <Avatar
                        variant="square"
                        className={classes.avatar}
                        src={client.brandImage + '&accessToken=' + authState.accessToken.accessToken}
                      />
                    </ListItemAvatar>
                    <ListItemText primary={client.displayName} className={classes.listItemText} />
                  </ListItem>
                );
              })}
            </List>
          </Scrollbars>
        )}
      </Popover>
    </div>
  );
};

export default TeamDropdown;
